import React, { useState, useEffect, useCallback } from 'react';
import { Headphones, Send, Clock, CheckCircle2, XCircle, AlertCircle, Zap } from 'lucide-react';
import { supportService } from '@/services/support.service';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/services/api';
import toast from 'react-hot-toast';
import type { SupportRequest, Agent } from '@/types';

const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'Pending', color: 'text-yellow-700 bg-yellow-50', icon: Clock },
  accepted: { label: 'Accepted', color: 'text-blue-700 bg-blue-50', icon: AlertCircle },
  rejected: { label: 'Rejected', color: 'text-red-700 bg-red-50', icon: XCircle },
  completed: { label: 'Completed', color: 'text-green-700 bg-green-50', icon: CheckCircle2 },
};

export const SupportPage: React.FC = () => {
  const { organization } = useAuthStore();
  const isFree = organization?.plan === 'free';

  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [agentId, setAgentId] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqData, agentRes] = await Promise.all([
        supportService.list(),
        apiClient.get('/agents'),
      ]);
      setRequests(reqData);
      setAgents(agentRes.data.data || []);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    setSubmitting(true);
    try {
      await supportService.create({
        agent_id: agentId || undefined,
        description: description.trim(),
      });
      toast.success('Support request submitted');
      setDescription('');
      setAgentId('');
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Support</h1>
        <p className="text-sm text-gray-500 mt-1">Request expert help with your AI agent setup</p>
      </div>

      {/* Free Plan Notice */}
      {isFree && (
        <div className="bg-amber-50 border-2 border-[#1a1a2e] rounded-xl p-4 flex items-start gap-3">
          <Zap className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Upgrade to access Support</p>
            <p className="text-xs text-amber-600 mt-1">
              Support requests are available on the Pay As You Go plan ($20/request). Upgrade from the Billing page to get started.
            </p>
          </div>
        </div>
      )}

      {/* Submit Form */}
      {!isFree && (
        <div className="bg-white border-2 border-[#1a1a2e] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">New Support Request</h3>
          </div>
          <p className="text-sm text-gray-500">
            Our team will help you configure your agent. You will be charged $20 when your request is accepted.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent (optional)</label>
              <select
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                className="w-full border-2 border-[#1a1a2e] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
              >
                <option value="">Select an agent...</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you need help with..."
                rows={5}
                className="w-full border-2 border-[#1a1a2e] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
              />
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || !description.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      )}

      {/* Request History */}
      <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#1a1a2e]">
          <h3 className="text-lg font-semibold text-gray-900">Request History</h3>
          <p className="text-sm text-gray-500">{requests.length} requests</p>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No support requests yet</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e]">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map((req) => {
                const config = statusConfig[req.status] || statusConfig.pending;
                const Icon = config.icon;
                return (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {req.agent_name || '—'}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700 max-w-xs truncate">
                      {req.description || '—'}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {config.label}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-400 text-right">
                      {new Date(req.created_at).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
